import { useState, useEffect } from 'react';
import axios from 'axios';
import { dashboardHandler } from '../DashboardHandler';
import { mapTopPerformersResponse } from '../utils/mappers';
import type { Performer } from '../types';

interface CachedData {
  performers: Performer[];
}

interface UseTopPerformersReturn {
  performers: Performer[];
  loading: boolean;
  error: string | null;
}

// Module-level cache shared across all hook instances
const globalCache: Record<string, CachedData> = {};

export const useTopPerformers = (period: string): UseTopPerformersReturn => {
  const [performers, setPerformers] = useState<Performer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check cache first
    if (globalCache[period]) {
      setPerformers(globalCache[period].performers);
      return;
    }

    const source = axios.CancelToken.source();
    let isMounted = true;

    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await dashboardHandler.getTopPerformers(period, source.token);

        if (isMounted) {
          const mappedPerformers = mapTopPerformersResponse(result);

          // Update cache
          globalCache[period] = { performers: mappedPerformers };

          setPerformers(mappedPerformers);
        }
      } catch (err: any) {
        if (isMounted && !axios.isCancel(err)) {
          console.error('Failed to fetch top performers:', err);
          setError(err.message || 'Failed to load top performers');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      source.cancel('Period changed or component unmounted');
    };
  }, [period]);

  return { performers, loading, error };
};

// Utility to clear cache if needed
export const clearTopPerformersCache = () => {
  Object.keys(globalCache).forEach((key) => delete globalCache[key]);
};
